/**
 * FALLBACK single-tab guard, for browsers with no Web Locks API (Safari <15.4, some embedded
 * webviews, any non-secure context). `softphoneOwnership.js` prefers Web Locks and only falls back
 * to this - see the trade-offs listed there.
 *
 * The policy is INCUMBENT KEEPS IT, which is the opposite of what this lock used to do. When the
 * softphone lived in a popup the newest claim winning was right: the only way to get a second
 * widget was to deliberately open one. Now that it lives in the header, every dashboard tab boots a
 * candidate, so "newest wins" would let an agent drop a live call by opening a tab to read a report.
 *
 * The election is therefore: ask who holds it, wait a beat, and claim only if nobody answered.
 */
export const SOFTPHONE_TAB_CHANNEL = 'ringbax-softphone-tab-lock'

// How long a claimant waits for an incumbent to answer its probe. Long enough for a busy tab to
// get a message handler onto the stack, short enough that the softphone is usable straight away.
export const ELECTION_TIMEOUT_MS = 250

// A released lock wakes every mirror at once, and they would all claim together. Spreading them
// over this window means the tie-break below is the exception rather than the rule.
export const HANDOVER_JITTER_MAX_MS = 150

/**
 * The wire. Exported so a spec can inject the one case a single-threaded test cannot otherwise
 * produce: two tabs that both believe they hold the lock, which needs a message to arrive after the
 * receiver has already claimed.
 */
export const TAB_LOCK_MESSAGES = Object.freeze({
  // claimant -> everyone, "does anyone hold this?"
  PROBE: 'probe',
  // holder -> everyone, "I do" - also the re-assertion that turns away a late claim
  HELD: 'held',
  // claimant -> everyone, "nobody answered, so I have taken it"
  CLAIM: 'claim',
  // holder -> everyone, "I am going away cleanly, someone take it"
  RELEASE: 'release',
  // taker -> everyone, "the agent asked for the softphone in my tab"
  STEAL: 'steal'
})

const { PROBE, HELD, CLAIM, RELEASE, STEAL } = TAB_LOCK_MESSAGES

export class SoftphoneTabLock {
  constructor(channelName = SOFTPHONE_TAB_CHANNEL) {
    this.tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    this.isHeld = false

    // When this tab took the lock. The tie-break below prefers the earlier holder, which is what
    // keeps an incumbent whose probe reply arrived late from being pushed off a live call.
    this.heldSince = null

    // Ownership was taken away - a deliberate takeover from another tab.
    this.onLost = null

    // The holder went away cleanly. Mirrors re-run the election on this.
    this.onReleased = null

    this.isSupported = typeof BroadcastChannel !== 'undefined'
    this.channel = this.isSupported ? new BroadcastChannel(channelName) : null

    this._election = null

    if (this.channel) {
      this.channel.onmessage = event => this._handleMessage(event?.data)
    }
  }

  /**
   * Runs the election. Resolves true when this tab may own the softphone.
   *
   * Also the on-demand path: a mirror whose holder crashed (no RELEASE was ever sent, so nothing
   * woke anyone) re-runs this when the agent next asks to go live, and finds the lock free.
   *
   * @returns {Promise<boolean>}
   */
  requestClaim() {
    if (this.isHeld) {
      return Promise.resolve(true)
    }

    // No channel means no way to ask and no way to be told. Single-tab enforcement is simply
    // unavailable; claiming keeps the softphone working for the one agent who has this browser.
    if (!this.channel) {
      this._take()

      return Promise.resolve(true)
    }

    if (this._election) {
      return this._election.promise
    }

    const election = { sawHolder: false, resolve: null, promise: null, timerId: null }

    election.promise = new Promise(resolve => {
      election.resolve = resolve
    })

    this._election = election

    this._post({ type: PROBE, tabId: this.tabId })

    election.timerId = setTimeout(() => this._settleElection(), ELECTION_TIMEOUT_MS)

    return election.promise
  }

  /**
   * Takes the lock away from whoever holds it. The agent asked for the softphone HERE, so no
   * election: the incumbent is told it lost and drops its transport.
   */
  steal() {
    this._abandonElection()
    this._take()
    this._post({ type: STEAL, tabId: this.tabId })
  }

  release() {
    this._abandonElection()

    if (!this.isHeld) {
      return
    }

    this.isHeld = false
    this.heldSince = null

    this._post({ type: RELEASE, tabId: this.tabId })
  }

  destroy() {
    this.release()

    this.onLost = null
    this.onReleased = null

    if (this.channel) {
      this.channel.onmessage = null
      this.channel.close()
      this.channel = null
    }
  }

  _take() {
    this.isHeld = true
    this.heldSince = Date.now()
  }

  _post(message) {
    if (!this.channel) {
      return
    }

    this.channel.postMessage(message)
  }

  _abandonElection() {
    if (!this._election) {
      return
    }

    const election = this._election

    this._election = null

    clearTimeout(election.timerId)
    election.resolve(false)
  }

  _settleElection() {
    const election = this._election

    if (!election) {
      return
    }

    this._election = null

    clearTimeout(election.timerId)

    if (election.sawHolder) {
      election.resolve(false)

      return
    }

    this._take()

    // Announced rather than assumed, so a tab that claimed at the same instant learns about this
    // one and the tie-break below picks a single winner.
    this._post({ type: CLAIM, tabId: this.tabId, heldSince: this.heldSince })

    election.resolve(true)
  }

  _loseTo(callback) {
    this.isHeld = false
    this.heldSince = null

    if (callback) {
      callback()
    }
  }

  /**
   * Who keeps the lock when two tabs both believe they hold it. The EARLIER holder wins, so a
   * genuine incumbent always beats a newcomer whose probe it failed to answer in time; the tab id
   * only decides a true tie, which is two tabs opened in the same millisecond.
   */
  _isOutrankedBy(otherHeldSince, otherTabId) {
    const mine = this.heldSince || 0
    const theirs = otherHeldSince || 0

    if (theirs !== mine) {
      return theirs < mine
    }

    return String(otherTabId) < String(this.tabId)
  }

  _handleMessage(message) {
    if (!message || message.tabId === this.tabId) {
      return
    }

    if (message.type === PROBE) {
      if (this.isHeld) {
        this._post({ type: HELD, tabId: this.tabId, heldSince: this.heldSince })
      }

      return
    }

    // Someone is asserting incumbency. A candidate stands down at once rather than waiting out its
    // timeout; a fellow holder means the two raced, so the earlier one keeps it.
    if (message.type === HELD) {
      if (this._election) {
        this._election.sawHolder = true
        this._settleElection()

        return
      }

      if (this.isHeld && this._isOutrankedBy(message.heldSince, message.tabId)) {
        this._loseTo(this.onLost)
      }

      return
    }

    // Another tab claimed. If this one holds the lock it does NOT stand down - it re-asserts, and
    // lets the claimant discover it spoke too soon. That asymmetry is the whole incumbent-keeps-it
    // policy: a claim can only ever be granted against silence.
    if (message.type === CLAIM) {
      if (this._election) {
        this._election.sawHolder = true
        this._settleElection()

        return
      }

      if (this.isHeld) {
        this._post({ type: HELD, tabId: this.tabId, heldSince: this.heldSince })
      }

      return
    }

    if (message.type === STEAL) {
      if (this.isHeld) {
        this._loseTo(this.onLost)
      }

      return
    }

    if (message.type === RELEASE) {
      if (this.isHeld || this._election) {
        return
      }

      if (this.onReleased) {
        this.onReleased()
      }
    }
  }
}

export default SoftphoneTabLock
